import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
    PendingProviderSelectionService,
    ProviderLinkService
} from '../../../core/services';
import { nextApplicationPage } from '../next-application-page';
import { CurrentApplicationService } from '../services';

/**
 * Everything that has to happen before the application flow renders: load the application, attach
 * any provider selection the student staged on the way in, and put them on the page they belong
 * on.
 *
 * Attaching the selection used to happen in PersonalInformationComponent.ngOnInit, which held the
 * only call that saved it. Correctness therefore depended on routing reaching that page, so a
 * student who changed providers after finishing Personal Information had the change silently
 * dropped (LA-22939).
 *
 * A guard rather than a resolver because it also decides *where* the student goes, and only a
 * guard can redirect. ApplicationComponent used to do that from its constructor, but once the
 * application was pre-loaded that ran during activation of the very route it was trying to
 * change, and the router discarded it — leaving every student on Personal Information whatever
 * their actual progress.
 */
@Injectable({
    providedIn: 'root'
})
export class ApplicationEntryGuard {
    constructor(
        private readonly _currentApplication: CurrentApplicationService,
        private readonly _pendingSelection: PendingProviderSelectionService,
        private readonly _providerLink: ProviderLinkService,
        private readonly _router: Router,
        private readonly _snackBar: MatSnackBar,
        private readonly _transloco: TranslocoService
    ) {}

    canActivate(
        _route: unknown,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> {
        this._currentApplication.reset();

        const staged = this._pendingSelection.has()
            ? this._pendingSelection.get()
            : undefined;

        // One call: the API creates the application if needed and attaches the selection before
        // returning it, so there is no round trip in which the record exists without the
        // student's choice on it.
        return this._currentApplication.load$(staged).pipe(
            tap(() => {
                // Only once the call succeeded — a failure leaves the selection staged for the
                // retry offered below.
                if (staged) this._pendingSelection.clear();
            }),
            map((application) => this._allowOrRedirect(application, state)),
            catchError((error) => this._sendBackToProviderSelection(error))
        );
    }

    /**
     * Returning `true` when the student is already on the right page is what stops this looping:
     * the redirect re-enters the route, the guard runs again, and it has to settle the second
     * time.
     */
    private _allowOrRedirect(
        application: Parameters<typeof nextApplicationPage>[0],
        state: RouterStateSnapshot
    ): boolean | UrlTree {
        const belongs = this._router.parseUrl(
            `/${nextApplicationPage(application)}`
        );
        if (belongs.toString() === state.url) return true;

        return belongs;
    }

    /**
     * Cancels the navigation and puts the student back where they can choose again.  Returning
     * EMPTY is what aborts it; the explicit navigate decides where they go instead.
     */
    private _sendBackToProviderSelection(error: unknown): Observable<never> {
        // A failure to load the application at all is not the student's to fix here, and
        // blocking entry would leave them with nowhere to go.
        if (!this._pendingSelection.has()) throw error;

        this._snackBar.open(
            this._transloco.translate('PROVIDER_SAVE_ERROR') ||
                'Unable to save your provider selection. Please select a provider again.',
            this._transloco.translate('CLOSE'),
            { duration: 10000 }
        );

        const providerId = this._providerLink.getProviderIdWithFallback();
        this._router.navigate([
            providerId
                ? `${runtimeConfig.clientCode}/providers/${providerId}`
                : `${runtimeConfig.clientCode}/provider`
        ]);

        return EMPTY;
    }
}
